import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, FileTextIcon, BrainIcon, CogIcon, WrenchIcon, BarChart3Icon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent as track } from '../../utils/analytics';
const EASE = [0.23, 1, 0.32, 1] as const;
type Bi = {
  fr: string;
  en: string;
};
const COPY = {
  eyebrow: {
    fr: 'Passez à l’action',
    en: 'Take action'
  },
  titleA: {
    fr: 'Transformez chaque demande',
    en: 'Turn every request'
  },
  titleB: {
    fr: 'en action.',
    en: 'into action.'
  },
  body: {
    fr: 'Voyez comment Requêtes ATNA peut s’intégrer à vos systèmes existants — ou remplacer votre processus actuel de bout en bout.',
    en: 'See how ATNA Requests can integrate with your existing systems — or replace your current process end to end.'
  },
  cta: {
    fr: 'Demander une démo',
    en: 'Request a demo'
  },
  ctaNote: {
    fr: 'Vos systèmes. Ou les nôtres. Une seule expérience.',
    en: 'Your systems. Or ours. One seamless experience.'
  }
};
const STEPS: {
  key: string;
  icon: typeof FileTextIcon;
  title: Bi;
  desc: Bi;
}[] = [{
  key: 'request',
  icon: FileTextIcon,
  title: {
    fr: 'Demande',
    en: 'Request'
  },
  desc: {
    fr: 'Soumise par un citoyen (application, portail, téléphone)',
    en: 'Submitted by a resident (app, portal, phone)'
  }
}, {
  key: 'understand',
  icon: BrainIcon,
  title: {
    fr: 'Comprend',
    en: 'Understands'
  },
  desc: {
    fr: 'Qualifie, enrichit et priorise avec l’IA',
    en: 'Qualifies, enriches and prioritizes with AI'
  }
}, {
  key: 'orchestrate',
  icon: CogIcon,
  title: {
    fr: 'Orchestre',
    en: 'Orchestrates'
  },
  desc: {
    fr: 'Affecte les bonnes équipes et planifie l’intervention',
    en: 'Assigns the right teams and plans the intervention'
  }
}, {
  key: 'act',
  icon: WrenchIcon,
  title: {
    fr: 'Agit',
    en: 'Acts'
  },
  desc: {
    fr: 'Les équipes terrain exécutent les tâches et font la mise à jour',
    en: 'Field teams execute and update progress'
  }
}, {
  key: 'measure',
  icon: BarChart3Icon,
  title: {
    fr: 'Mesure',
    en: 'Measures'
  },
  desc: {
    fr: 'Performance, SLA et impact pour mieux agir',
    en: 'Performance, SLAs and impact to improve decisions'
  }
}];
function Connector({
  vertical = false
}: {
  vertical?: boolean;
}) {
  const Icon = vertical ? ArrowDownIcon : ArrowRightIcon;
  return <div className={`relative flex items-center justify-center ${vertical ? 'h-10 w-full' : 'w-12 xl:w-16 self-stretch'}`} aria-hidden="true" data-id="element-6351" data-mp="3gpskturya75s">
      <span className={`absolute bg-[#DAD7F7] ${vertical ? 'top-0 bottom-0 left-1/2 w-px' : 'left-0 right-0 top-1/2 h-px'}`} data-id="element-6352" data-mp="3qp1mzawyys38" />
      <span className="relative w-8 h-8 rounded-full bg-white border border-[#E6E4F8] shadow-[0_4px_10px_-6px_rgba(79,70,229,0.4)] flex items-center justify-center" data-id="element-6353" data-mp="3vi47ktjrqr7a">
        <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={2} data-id="element-6354" data-mp="32zp7xueynpt0" />
      </span>
    </div>;
}
export function RequestsFinalCTA() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (v: Bi) => isFR ? v.fr : v.en;
  return <motion.div initial={{
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.3,
    ease: EASE
  }} className="relative max-w-[1440px] mx-auto overflow-hidden rounded-[28px] border border-[#ECEBF7] bg-[#FAFAFE] shadow-[0_30px_60px_-50px_rgba(40,36,120,0.35)]" data-id="element-6355" data-mp="1g1y32cyh4va9">
      {/* Soft lavender atmospheric curves */}
      <svg className="pointer-events-none absolute inset-0 w-full h-full" viewBox="0 0 1440 460" preserveAspectRatio="none" aria-hidden="true" data-id="element-6356" data-mp="se7uiefyu7qt">
        <path d="M-60 460 C 60 250, 260 150, 520 120 L -60 120 Z" fill="#F1F0FC" opacity="0.7" data-id="element-6357" data-mp="ngemrgz0l5of" />
        <path d="M980 460 C 1040 180, 1260 70, 1500 90 L 1500 460 Z" fill="#F0EFFC" opacity="0.75" data-id="element-6358" data-mp="iepa5ww58jy8" />
        <path d="M1120 460 C 1170 250, 1320 160, 1500 170 L 1500 460 Z" fill="#EBE9FB" opacity="0.6" data-id="element-6359" data-mp="di8nl74ledn9" />
      </svg>

      <div className="relative px-6 sm:px-10 lg:px-14 xl:px-20 pt-12 lg:pt-14 pb-10 lg:pb-12" data-id="element-6360" data-mp="2djowa9bc8ivg">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-10 lg:gap-16 items-center" data-id="element-6361" data-mp="2ihqiv47z30bo">
          <div data-id="element-6362" data-mp="23t3t08kpyq67">
            <p className="font-sans text-[12.5px] font-semibold tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-6363" data-mp="23rj87ao7illj">{pick(COPY.eyebrow)}</p>
            <h2 className="font-serif text-[2.4rem] sm:text-[3rem] xl:text-[3.6rem] text-[#121212] leading-[1.05] tracking-tight mt-4 max-w-[640px]" data-id="element-6364" data-mp="1us5w0a8p7ix4">
              {pick(COPY.titleA)} <span className="text-[#4F46E5]" data-id="element-6365" data-mp="1zpflfpw37uky">{pick(COPY.titleB)}</span>
            </h2>
            <p className="font-sans text-[17px] md:text-[19px] text-[#5B6478] leading-[1.55] mt-5 max-w-[780px]" data-id="element-6366" data-mp="1kwm5y4gtfega">{pick(COPY.body)}</p>
          </div>

          <div className="lg:w-[340px] xl:w-[380px] text-center" data-id="element-6367" data-mp="3f8amgb2p3p2s">
            <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
            location: 'Requests Final'
          })} className="flex items-center justify-center gap-3 rounded-lg bg-[#4F46E5] px-8 py-4 font-sans text-[17px] font-semibold text-white shadow-[0_14px_30px_-16px_rgba(79,70,229,0.8)] transition-colors duration-150 hover:bg-[#4338CA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4F46E5]" data-id="element-6368" data-mp="3k7n5slucye0m">
              {pick(COPY.cta)}
              <ArrowUpRightIcon className="w-[18px] h-[18px]" strokeWidth={2} aria-hidden="true" data-id="element-6369" data-mp="1r9qc3eta9732" />
            </Link>
            <p className="font-sans text-[14px] text-[#5B6478] mt-4" data-id="element-6370" data-mp="1n8mwmtf5dd2d">{pick(COPY.ctaNote)}</p>
          </div>
        </div>

        {/* Five-step flow */}
        <ol className="mt-10 lg:mt-12 flex flex-col xl:flex-row xl:items-stretch" data-id="element-6371" data-mp="1wa4ae55312nw">
          {STEPS.map((s, i) => {
          const Icon = s.icon;
          return <Fragment key={s.key} data-id="element-6372" data-mp="6wrd2zve0ewa" data-mpi={i}>
                <li className="flex-1 min-w-0 rounded-2xl border border-[#EEEDF7] bg-white px-4 py-4 flex items-center gap-4 shadow-[0_10px_24px_-20px_rgba(40,36,120,0.35)]" data-id="element-6373" data-mp="1wd6lm9fpd0d">
                  <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-6374" data-mp="26x63pdmp8xd9">
                    <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.7} data-id="element-6375" data-mp="2axpwy645jgu4" />
                  </span>
                  <span className="min-w-0" data-id="element-6376" data-mp="2fyx90hdbv26l">
                    <span className="block font-sans text-[15.5px] font-semibold text-[#121212]" data-id="element-6377" data-mp="2fvhqf4kg3b0f">{pick(s.title)}</span>
                    <span className="block font-sans text-[12.5px] text-[#5B6478] leading-snug mt-1" data-id="element-6378" data-mp="2pvs56tm4oqhp">{pick(s.desc)}</span>
                  </span>
                </li>
                {i < STEPS.length - 1 && <li className="flex justify-center" aria-hidden="true" data-id="element-6379" data-mp="2yovbmhycw32u">
                    <span className="hidden xl:flex h-full" data-id="element-6380" data-mp="33ngfcrcth4w1">
                      <Connector data-id="element-6381" data-mp="33ozw0j2dxhyc" />
                    </span>
                    <span className="flex xl:hidden w-full" data-id="element-6382" data-mp="38lzykk0102kp">
                      <Connector vertical data-id="element-6383" data-mp="38k6th9529qet" />
                    </span>
                  </li>}
              </Fragment>;
        })}
        </ol>
      </div>
    </motion.div>;
}